import { useEffect, useRef, useState } from "react";
import type { GalleryPhoto } from "@tih/shared";
import { PhotoLightbox } from "./PhotoLightbox";

const PHOTO_BATCH = 24;

type Props = {
  photos: GalleryPhoto[];
};

export function GalleryMasonry({ photos }: Props) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [visibleCount, setVisibleCount] = useState(PHOTO_BATCH);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setVisibleCount(PHOTO_BATCH);
  }, [photos]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || visibleCount >= photos.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleCount((count) => Math.min(count + PHOTO_BATCH, photos.length));
        }
      },
      { rootMargin: "600px 0px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [visibleCount, photos.length]);

  if (photos.length === 0) {
    return (
      <div className="gal-empty">
        <p>No photos in this album yet.</p>
      </div>
    );
  }

  const visiblePhotos = photos.slice(0, visibleCount);
  const hasMore = visibleCount < photos.length;

  return (
    <>
      <div className="gal-masonry">
        {visiblePhotos.map((photo, i) => (
          <button
            type="button"
            key={photo.id}
            className="gal-masonry-item"
            onClick={() => setLightboxIndex(i)}
            aria-label={photo.caption ?? `Photo ${i + 1}`}
          >
            <img
              src={photo.thumbUrl ?? photo.url}
              alt={photo.caption ?? ""}
              loading="lazy"
              decoding="async"
              width={photo.width ?? undefined}
              height={photo.height ?? undefined}
            />
            {photo.commentCount > 0 && (
              <span className="gal-masonry-comments" aria-hidden="true">
                {photo.commentCount}
              </span>
            )}
            {photo.caption && (
              <span className="gal-masonry-caption">{photo.caption}</span>
            )}
          </button>
        ))}
      </div>

      {hasMore && (
        <div ref={sentinelRef} className="gal-masonry-sentinel" aria-hidden="true">
          <p className="gal-masonry-loading">Loading more photos…</p>
        </div>
      )}

      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={photos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </>
  );
}
